﻿/**
 * 用于创建 Swiper 组件，并通过 data-config 配置参数。
 *
 * 通过 snippets/header-script.liquid 全站加载一次。页面未预加载 Swiper 时，
 * 会通过 window.DreameAssets.loadSwiper() 按需加载并复用同一 Promise。
 * IIFE + 幂等守卫，重复引入也不会二次声明或重复注册。
 *
 * 若配置了 autoplay，默认会等首张可见主图加载完成（或超时）后再开始自动播放，
 * 避免慢网下一直切到 image-placeholder。可用 data-wait-first-image="false" 关闭。
 *
 * @class SwiperSlider
 * @extends {HTMLElement}
 *
 * @example
 *
 * <swiper-slider
 *   data-config='{
 *     "slidesPerView": 4,
 *     "spaceBetween": 24,
 *     "loop": true,
 *     "autoplay": {
 *       "delay": 3000
 *     }
 *   }'
 * >
 *
 * <div class="swiper">
 *   <div class="swiper-wrapper">
 *     <div class="swiper-slide"></div>
 *   </div>
 *
 *   <div class="swiper-pagination"></div>
 *   <button class="swiper-button-prev"></button>
 *   <button class="swiper-button-next"></button>
 * </div>
 * </swiper-slider>
 */
(function () {
  "use strict";

  if (customElements.get("swiper-slider")) return;

  var AUTOPLAY_ARM_FALLBACK_MS = 8000;

  class SwiperSlider extends HTMLElement {
    constructor() {
      super();

      this.swiper = null;
      this._autoplayArmTimer = null;
      this._autoplayArmImg = null;
      this._autoplayArmOnDone = null;
      this._initializing = false;
    }

    connectedCallback() {
      this.init();
    }

    disconnectedCallback() {
      this.destroy();
    }

    async init() {
      if (this._initializing || this.swiper) return;

      this._initializing = true;

      try {
        if (!window.Swiper) {
          if (!window.DreameAssets?.loadSwiper) {
            throw new Error("DreameAssets.loadSwiper is unavailable");
          }

          await window.DreameAssets.loadSwiper();
        }

        if (!this.isConnected) return;

        const container = this.querySelector(".swiper");

        if (!container) {
          console.warn("Swiper container not found");
          return;
        }

        this.destroy();

        const userConfig = this.getConfig();
        const tabItems = this.querySelectorAll(".tab-item");
        const tabIndicator = this.querySelector(".tab-indicator");
        const sliderElement = this;

        const defaultConfig = {
          slidesPerView: 1,
          slidesPerGroup: 1,
          spaceBetween: 0,

          speed: 500,

          watchOverflow: true,

          observer: true,
          observeParents: true,

          navigation: {
            nextEl: this.querySelector(".swiper-button-next"),
            prevEl: this.querySelector(".swiper-button-prev"),
          },

          pagination: {
            el: this.querySelector(".swiper-pagination"),
            clickable: true,
            renderBullet(index, className) {
              return `
            <span class="${className}">
              <span class="bullet-progress"></span>
            </span>
          `;
            },
          },

          on: {
            autoplayTimeLeft(swiper, time, progress) {
              Array.prototype.forEach.call(swiper.pagination.bullets, (bullet, index) => {
                const bar = bullet.querySelector(".bullet-progress");

                if (index === swiper.realIndex) {
                  bar.style.width = `${(1 - progress) * 100}%`;
                } else {
                  bar.style.width = "0%";
                }
              });
            },

            slideChange: function (swiper) {
              swiper = swiper || this;
              sliderElement.updateFraction(swiper);
              sliderElement.updateActiveIndex(swiper);

              const bullets = swiper.pagination?.bullets || [];

              Array.prototype.forEach.call(bullets, (bullet, index) => {
                const bar = bullet.querySelector(".bullet-progress");

                if (!bar) return;

                if (index !== swiper.realIndex || swiper.autoplay?.running) {
                  bar.style.width = "0%";
                } else {
                  bar.style.width = "100%";
                }
              });

              if (tabItems.length) {
                tabItems.forEach((item) => {
                  const isActive = Number(item.dataset.index) === swiper.realIndex;
                  item.classList.toggle("active", isActive);

                  if (item.getAttribute("role") === "tab") {
                    item.setAttribute("aria-selected", isActive ? "true" : "false");
                  }
                });

                if (tabIndicator) {
                  const activeIndex = [...tabItems].findIndex((item) => item.classList.contains("active"));
                  const tabCount = tabItems.length;

                  if (tabCount > 2) {
                    tabIndicator.style.width = `${100 / tabCount}%`;
                    tabIndicator.style.transform =
                      activeIndex > 0 ? `translateX(calc(${activeIndex} * 100%))` : "translateX(0)";
                  } else {
                    tabIndicator.style.transform =
                      activeIndex > 0 ? `translateX(calc(100% + 4px))` : "translateX(0)";
                  }
                }
              }
            },
          },
        };

        const config = this.deepMerge(defaultConfig, userConfig);
        const paginationEl = this.querySelector(".swiper-pagination");
        const hasAutoplay = Boolean(config.autoplay);
        const waitForFirstImage = hasAutoplay && this.dataset.waitFirstImage !== "false";

        // 如果 paginationEl 包含 style-pagination 类，则添加 is-autoplay 类
        // style-pagination 即progress 指示器，对应snippets/swiper-pagination
        if (paginationEl?.classList.contains("style-pagination")) {
          paginationEl.classList.toggle("is-autoplay", hasAutoplay);
        }

        this.swiper = new window.Swiper(container, config);
        this.updateFraction(this.swiper);
        this.updateActiveIndex(this.swiper);

        if (waitForFirstImage && this.swiper.autoplay) {
          this.swiper.autoplay.stop();
          this.armAutoplayAfterFirstImage();
        }

        if (tabItems.length) {
          tabItems.forEach((item) => {
            item.addEventListener("click", () => {
              const index = Number(item.dataset.index);
              if (!isNaN(index) && this.swiper) {
                this.swiper.slideTo(index);
              }
            });
          });
        }
      } catch (error) {
        console.error("Swiper initialization failed", error);
      } finally {
        this._initializing = false;
      }
    }

    /**
     * 等当前激活 slide 的主图 load/error，或超时后，再启动 autoplay。
     */
    armAutoplayAfterFirstImage() {
      var self = this;
      var started = false;

      function start() {
        if (started) return;
        started = true;
        self.clearAutoplayArm();

        if (!self.swiper || !self.swiper.autoplay) return;
        self.swiper.autoplay.start();
      }

      this._autoplayArmTimer = setTimeout(start, AUTOPLAY_ARM_FALLBACK_MS);

      var slide =
        (this.swiper.slides && this.swiper.slides[this.swiper.activeIndex]) ||
        this.querySelector(".swiper-slide-active") ||
        this.querySelector(".swiper-slide");

      var mainImg = (slide && slide.querySelector("image-loader img")) || (slide && slide.querySelector("img"));

      if (!mainImg) {
        start();
        return;
      }

      if (mainImg.complete && mainImg.naturalWidth > 0) {
        start();
        return;
      }

      this._autoplayArmImg = mainImg;
      this._autoplayArmOnDone = start;
      mainImg.addEventListener("load", start);
      mainImg.addEventListener("error", start);
    }

    clearAutoplayArm() {
      if (this._autoplayArmTimer) {
        clearTimeout(this._autoplayArmTimer);
        this._autoplayArmTimer = null;
      }

      if (this._autoplayArmImg && this._autoplayArmOnDone) {
        this._autoplayArmImg.removeEventListener("load", this._autoplayArmOnDone);
        this._autoplayArmImg.removeEventListener("error", this._autoplayArmOnDone);
      }

      this._autoplayArmImg = null;
      this._autoplayArmOnDone = null;
    }

    destroy() {
      this.clearAutoplayArm();

      if (!this.swiper) return;

      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    refresh() {
      this.swiper?.update();
    }

    updateFraction(swiper) {
      const current = this.querySelector("[data-swiper-fraction-current]");
      const total = this.querySelector("[data-swiper-fraction-total]");

      if (!current || !total || !swiper) return;

      const slides = this.querySelectorAll(".swiper-wrapper > .swiper-slide:not(.swiper-slide-duplicate)");
      current.textContent = String((swiper.realIndex || 0) + 1).padStart(2, "0");
      total.textContent = String(slides.length).padStart(2, "0");
    }

    updateActiveIndex(swiper) {
      if (!swiper) return;

      const index = typeof swiper.realIndex === "number" ? swiper.realIndex : swiper.activeIndex || 0;
      this.setAttribute("active-index", String(index));
    }

    getConfig() {
      try {
        return JSON.parse(this.dataset.config || "{}");
      } catch (error) {
        console.error("swiper config parse error", error);

        return {};
      }
    }

    deepMerge(target, source) {
      const result = {
        ...target,
      };

      for (const key in source) {
        const value = source[key];

        if (value && typeof value === "object" && !Array.isArray(value)) {
          result[key] = this.deepMerge(target[key] || {}, value);
        } else {
          result[key] = value;
        }
      }

      return result;
    }
  }

  customElements.define("swiper-slider", SwiperSlider);
})();
